import type { Farm } from "@/services";
import type { Vote } from "@/types";
import { beauty, calculatePercentage, truncateAPYDecimals } from "@/utils";
import type { FarmWeights } from "./types";

export interface GetProcessedVotesProps {
    farms: Farm[];
    currentVoteDistribution: Vote[];
    previousVoteDistribution?: Vote[];
    selectedVoteWeights: number[];
    totalVotingPower: number;
}

export function getProcessedVotes({
    farms,
    currentVoteDistribution,
    previousVoteDistribution,
    selectedVoteWeights,
    totalVotingPower,
}: GetProcessedVotesProps) {
    return farms.map((farm: Farm, index) => {
        const currentVote = currentVoteDistribution.find(
            (x: Vote) => x.farmAddress.toLowerCase() === farm.address.toLowerCase(),
        );
        const previousVote =
            previousVoteDistribution?.find((x: Vote) => x.farmAddress.toLowerCase() === farm.address.toLowerCase()) ??
            null;

        const currentPercentage = (Number(currentVote?.percentage) ?? 0).toFixed(0);

        const votePercentage = selectedVoteWeights[index] ?? 0;
        const voteAmount = calculatePercentage(totalVotingPower, Number(votePercentage));

        return {
            farmAddress: farm.address,
            assetName: farm.label ?? farm.name ?? "",
            apr: truncateAPYDecimals(`${(farm.APY ?? 0) * 100}`, 2),
            tvl: beauty(farm.assetsNormalized ?? 0),
            currentPercentage: currentPercentage,
            lastWeekPercentage: previousVote?.percentage.toFixed(0) ?? "0.0",
            totalVotes: beauty(Number(currentVote?.votesCast || 0)),
            votePercentage,
            voteAmount,
        };
    });
}

export function applyLiquidFarmFilter(farms: Farm[]) {
    return farms.filter((farm: Farm) => farm.maturityTimestampMs === 0 && farm.type !== "PROTOCOL");
}

export function applyLockedFarmFilter(farms: Farm[]) {
    return farms.filter((farm: Farm) => farm.bucket !== 0);
}

export function applyLockedFarmFilterByDuration(farms: Farm[], duration: number) {
    return farms.filter((farm: Farm) => farm.bucket <= duration && farm.bucket !== 0);
}

export function formatFarmWeights(farms: Farm[], selectedVoteWeights: number[]) {
    const farmWeights: FarmWeights = {};
    for (let i = 0; i < farms.length; i++) {
        farmWeights[farms[i].address] = selectedVoteWeights[i];
    }
    return farmWeights;
}
